
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '@/components/Hero';
import TrustRow from '@/components/TrustRow';
import WhoWeHelp from '@/components/WhoWeHelp';
import Services from '@/components/Services';
import TrainingCertifications from '@/components/TrainingCertifications';
import SecurityAssessments from '@/components/SecurityAssessments';
import Outcomes from '@/components/Outcomes';
import CohortBand from '@/components/CohortBand';
import Process from '@/components/Process';
import IdealCustomer from '@/components/IdealCustomer';
import Testimonials from '@/components/Testimonials';
import Blog from '@/components/Blog';
import FAQSection from '@/components/seo/FAQSection';
import CitedSources from '@/components/seo/CitedSources';
import { HOMEPAGE_FAQS } from '@/components/seo/faqs';
import { ESSENTIAL_EIGHT_SOURCES } from '@/components/seo/citations';
import CTASection from '@/components/CTASection';

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Educ4te",
  "url": "https://educ4te.com",
  "logo": "https://educ4te.com/images/educ4te.logos/educ4te.png",
  "description": "Australian cybersecurity training, assessments, and advisory services specialising in Essential Eight and Microsoft 365 security.",
  "areaServed": "AU",
  "inLanguage": "en-AU",
  "sameAs": ["https://www.linkedin.com/company/educ4te"]
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Educ4te",
  "url": "https://educ4te.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://educ4te.com/blog?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const LandingPage = () => {
  return (
    <>
      <Helmet>
        {/* Must stay a SINGLE expression child. react-helmet-async passes <title>
            children straight to React 19's hoisting path, which silently renders
            an EMPTY <title> when children is an array (`{expr} - literal`). (#339) */}
        <title>{`Microsoft Security & AI Training Australia | Educ4te`}</title>
        <link rel="canonical" href="https://educ4te.com/" />
        <meta property="og:locale" content="en_AU" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>
      <Hero />
      <TrustRow />
      <WhoWeHelp />
      <Services />
      <TrainingCertifications />
      <SecurityAssessments />
      <CohortBand />
      <Outcomes />
      <Process />
      <IdealCustomer />
      <Testimonials />
      <Blog />
      <FAQSection faqs={HOMEPAGE_FAQS} title="Frequently asked questions" />

      {/* Related from Educ4te — in-body cross-links for AEO/GEO */}
      <section className="py-12 px-6 bg-white border-t border-[#E8E0F0]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-[#341671] mb-6">Related from Educ4te</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link
              to="/copilot-readiness"
              className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors"
            >
              <h3 className="text-base font-semibold text-[#341671] mb-2">Copilot AI Readiness Assessment</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Free 15-minute scored assessment across eight Copilot readiness domains, mapped to ACSC Essential Eight.</p>
            </Link>
            <Link
              to="/assessments"
              className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors"
            >
              <h3 className="text-base font-semibold text-[#341671] mb-2">Microsoft 365 security assessments</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Fixed-price assessments covering identity, SharePoint, Active Directory, and Essential Eight maturity.</p>
            </Link>
            <Link
              to="/resources"
              className="block p-5 rounded-xl border border-[#E8E0F0] bg-[#F4F0FA] hover:bg-[#EDE8F5] transition-colors"
            >
              <h3 className="text-base font-semibold text-[#341671] mb-2">Free resource library</h3>
              <p className="text-sm text-[#6B5E82] leading-relaxed">Playbooks, checklists, and reference cards covering Copilot governance, Essential Eight, and Privacy Act obligations.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <CitedSources
            title="Authoritative sources"
            sources={ESSENTIAL_EIGHT_SOURCES.slice(0, 3)}
          />
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default LandingPage;
